import { useMemo } from 'react';

import { Button } from '@/components/ui/button';

interface MemberInviteLandingPageProps {
  path: string;
  login: (returnUrl?: string, options?: { register?: boolean }) => void;
}

export default function MemberInviteLandingPage({ path, login }: MemberInviteLandingPageProps) {
  const inviteToken = useMemo(() => {
    const search = path.includes('?') ? path.slice(path.indexOf('?') + 1) : '';
    return new URLSearchParams(search).get('member_invite')?.trim() ?? '';
  }, [path]);

  const returnUrl = useMemo(() => {
    if (typeof window === 'undefined') {
      return path;
    }
    return `${window.location.origin}${path}`;
  }, [path]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-16 text-foreground">
      <div className="w-full max-w-3xl space-y-6">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Tenant invite</p>
          <h1 className="text-3xl font-semibold text-foreground">Join an existing tenant</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            This invite adds your account to an existing tenant workspace. Sign in if you already have a portal account,
            or create one first and we will bring you back to this invite automatically.
          </p>
        </div>

        <div className="rounded-2xl border border-border/60 bg-card/70 p-6 shadow-sm">
          <div className="space-y-4">
            <div className="rounded-xl border border-dashed border-border/70 bg-background/60 p-4 text-sm text-muted-foreground">
              {inviteToken
                ? 'The invite token is attached to this link already. Use one of the actions below to continue.'
                : 'This invite link is missing its token. Ask your tenant administrator for a fresh invite.'}
            </div>

            <div className="flex flex-wrap gap-3">
              <Button onClick={() => login(returnUrl, { register: true })} disabled={!inviteToken}>
                Create account
              </Button>
              <Button variant="outline" onClick={() => login(returnUrl)} disabled={!inviteToken}>
                Sign in
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
